import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserEntity,
  useFetchManyUsersQuery,
  useLazyFetchUserProfileQuery,
  AdminRole,
} from 'src/store/userApi';
import { useSelector } from 'react-redux';
import { selectUser } from 'src/store/userSlice';
import { UserConversationEntity } from 'src/store/chatApi';
import { Alert } from 'src/ui/Alert';
import { Badge } from 'src/ui/Badge';
import { Select } from 'src/ui/Select';
import { Spinner } from 'src/ui/Spinner';
import { PageHead } from 'src/ui/patterns/PageHead';
import ConversationList from './conversationList';
import MessageView from './messageView';

const ConversationHistoryPage = () => {
  const [selectedUser, setSelectedUser] = useState<UserEntity | null>(null);
  const [selectedConversation, setSelectedConversation] =
    useState<UserConversationEntity | null>(null);
  const currentUser = useSelector(selectUser);
  const [searchParams] = useSearchParams();

  const {
    data: usersData,
    isLoading,
    error,
  } = useFetchManyUsersQuery({ limit: 100 });
  const [fetchUserProfile] = useLazyFetchUserProfileQuery();

  const isAdmin =
    currentUser?.adminRole && currentUser.adminRole >= AdminRole.ADMIN;

  const onSetUser = useCallback(
    async (userId: string) => {
      if (!userId) {
        setSelectedUser(null);
        setSelectedConversation(null);
        return;
      }
      try {
        const result = await fetchUserProfile(userId).unwrap();
        setSelectedUser(result);
        setSelectedConversation(null);
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setSelectedUser(null);
        setSelectedConversation(null);
      }
    },
    [fetchUserProfile]
  );

  const onSelectConversation = (conversation: UserConversationEntity) => {
    setSelectedConversation(conversation);
  };

  // Handle URL parameter for pre-selecting user
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId && usersData?.data && !selectedUser) {
      onSetUser(userId);
    }
  }, [searchParams, usersData, selectedUser, onSetUser]);

  const userConversationId = searchParams.get('userConversationId');

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '340px 1fr',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* LEFT — user picker + conversation list */}
      <div
        className="col"
        style={{ borderRight: '1px solid var(--border)', overflow: 'hidden' }}
      >
        {/* Page header */}
        <PageHead
          title="Conversations"
          subtitle={
            usersData?.data
              ? `${usersData.data.length} users available`
              : undefined
          }
          right={
            currentUser && (
              <Badge color={isAdmin ? 'green' : 'red'} size="sm">
                {isAdmin ? 'Admin' : 'No Access'}
              </Badge>
            )
          }
        />

        {/* User selector */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          {isLoading ? (
            <div
              className="row gap-2"
              style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}
            >
              <Spinner size="sm" />
              Loading users…
            </div>
          ) : error ? (
            <Alert color="red" title="Failed to load users" />
          ) : !isAdmin ? (
            <Alert
              color="amber"
              title="Admin access required to view conversations"
            />
          ) : (
            <Select
              value={selectedUser?.id || ''}
              onChange={(e) => onSetUser(e.target.value)}
            >
              <option value="">Select a user to view conversations</option>
              {usersData?.data?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Conversation list */}
        <div className="col grow" style={{ overflow: 'hidden' }}>
          <ConversationList
            selectedUser={selectedUser}
            selectedConversation={selectedConversation}
            onSelectConversation={onSelectConversation}
            preSelectConversationId={userConversationId}
          />
        </div>
      </div>

      {/* RIGHT — message view */}
      <div style={{ overflow: 'hidden' }}>
        <MessageView
          selectedUser={selectedUser}
          selectedConversation={selectedConversation}
        />
      </div>
    </div>
  );
};

export default ConversationHistoryPage;
